import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";
import { getChecklistQrInfo } from "src/pages/api";
import PrintableClientQrCodes from "../client-qr-code/PrintableClientQrCodes";

// -─ Main dialog 
export default function QrDownloadDialog({ open, onClose, checklist }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]); // selected clientIds
  const [printing, setPrinting] = useState(false);

  // Print state: what's currently loaded into the hidden print sheet
  const printRef = useRef(null);
  const [printPayload, setPrintPayload] = useState({ clientName: "", qrCodes: [] });

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printPayload.clientName
      ? `${printPayload.clientName}-qr-codes`
      : "qr-codes",
    onAfterPrint: () => setPrinting(false),
  });

  // - Load QR info when dialog opens 
  useEffect(() => {
    if (!open || !checklist?.id) return;

    setLoading(true);
    setError("");
    setClients([]);
    setSelected([]);

    getChecklistQrInfo({ id: checklist.id })
      .then((info) => {
        const clientList = (info?.clients || []).map((c) => ({
          id: c.clientId,
          clientName: c.clientName,
          qrCodes: [{
            code: info.qrCode,
            qrCodeName: info.checklistName,
            companyName: info.company?.companyName,
            companyLogo: info.company?.companyLogo,
            clientName: c.clientName,
          }],
        }));
        setClients(clientList);
        setSelected(clientList.map((c) => c.id)); // pre-select all
      })
      .catch((err) => setError(err?.message ?? "Failed to load QR info."))
      .finally(() => setLoading(false));
  }, [open, checklist?.id]);

  const allSelected = clients.length > 0 && selected.length === clients.length;
  const someSelected = selected.length > 0 && !allSelected;

  const toggleAll = () =>
    setSelected(allSelected ? [] : clients.map((c) => c.id));

  const toggleOne = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // - Print: merge all selected clients' QR codes into one sheet -------
  const handleDownload = () => {
    if (!selected.length) return;
    setPrinting(true);

    const selectedClients = clients.filter((c) => selected.includes(c.id));

    const mergedQrCodes = selectedClients.flatMap((c) =>
      (c.qrCodes || []).map((qr) => ({
        ...qr,
        qrCodeName:
          selectedClients.length > 1
            ? `${c.clientName} – ${qr.qrCodeName || qr.code}`
            : qr.qrCodeName,
      }))
    );

    const sheetClientName =
      selectedClients.length === 1
        ? selectedClients[0].clientName
        : checklist?.name ?? "All Clients";

    flushSync(() => {
      setPrintPayload({ clientName: sheetClientName, qrCodes: mergedQrCodes });
    });

    handlePrint();
  };

  const hasNoClients = !loading && !error && clients.length === 0;
  const hasClients = !loading && !error && clients.length > 0;

  return (
    <>
      {/* Hidden printable sheet - rendered off-screen */}
      <Box sx={{ position: "fixed", left: -99999, top: -99999, zIndex: -1 }}>
        <PrintableClientQrCodes
          ref={printRef}
          clientName={printPayload.clientName}
          qrCodes={printPayload.qrCodes}
        />
      </Box>

      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography fontWeight={700} fontSize="1rem">
            Download QR Codes
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2, pb: 3 }}>
          {/* Checklist name */}
          {checklist?.name && (
            <Typography variant="body2" sx={{ color: "#6B7280", mb: 2 }}>
              Checklist:{" "}
              <strong style={{ color: "#111827" }}>{checklist.name}</strong>
            </Typography>
          )}

          {/* Loading */}
          {loading && (
            <Stack alignItems="center" py={3} spacing={1}>
              <CircularProgress size={28} sx={{ color: "#2563EB" }} />
              <Typography variant="body2" color="text.secondary">
                Loading assigned clients…
              </Typography>
            </Stack>
          )}

          {/* Error */}
          {!loading && error && (
            <Typography variant="body2" color="error" sx={{ py: 2 }}>
              {error}
            </Typography>
          )}

          {/* No clients */}
          {hasNoClients && (
            <Typography
              variant="body2"
              sx={{ color: "#9CA3AF", py: 2, textAlign: "center" }}
            >
              No clients are assigned to this checklist.
            </Typography>
          )}

          {/* Client checkbox list */}
          {hasClients && (
            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                overflow: "hidden",
                mb: 3,
              }}
            >
              {/* Select all */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: "#F9FAFB",
                  borderBottom: "1px solid #E5E7EB",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                      sx={{
                        color: "#2563EB",
                        "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                          color: "#2563EB",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography fontWeight={700} fontSize="0.83rem" color="#374151">
                      Select All ({clients.length})
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Box>

              {/* Per-client rows with QR count badge */}
              <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
                {clients.map((client, idx) => (
                  <React.Fragment key={client.id}>
                    <Box
                      sx={{
                        px: 2,
                        py: 0.9,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        "&:hover": { backgroundColor: "#F5F8FF" },
                        transition: "background 0.15s",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={selected.includes(client.id)}
                            onChange={() => toggleOne(client.id)}
                            sx={{
                              color: "#2563EB",
                              "&.Mui-checked": { color: "#2563EB" },
                            }}
                          />
                        }
                        label={
                          <Typography fontSize="0.85rem" color="#111827" fontWeight={500}>
                            {client.clientName}
                          </Typography>
                        }
                        sx={{ m: 0, flex: 1 }}
                      />
                      {/* QR count badge */}
                      <Typography
                        fontSize="0.75rem"
                        sx={{
                          color: "#6B7280",
                          backgroundColor: "#F3F4F6",
                          px: 1,
                          py: 0.3,
                          borderRadius: "999px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {client.qrCodes.length} QR{client.qrCodes.length !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                    {idx < clients.length - 1 && <Divider sx={{ mx: 2 }} />}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}

          {/* Print / Download button */}
          {!hasNoClients && (
            <Button
              fullWidth
              variant="contained"
              disabled={selected.length === 0 || printing || loading}
              onClick={handleDownload}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                py: 1.3,
                backgroundColor: "#2563EB",
                "&:hover": { backgroundColor: "#1D4ED8" },
                "&.Mui-disabled": { backgroundColor: "#E5E7EB", color: "#9CA3AF" },
              }}
            >
              {printing ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                  <span>Preparing…</span>
                </Stack>
              ) : (
                `Print / Download QR${selected.length > 0 ? ` (${selected.length})` : ""}`
              )}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
